import deepmerge from "@fastify/deepmerge";
import { Task } from "@lit/task";
import {
    DASHBOARD_CONFIG,
    defaultDashboardConfig,
    formatDate,
    getDateRangeFromDays,
    getMonthRange,
    GetTimeLogEventsParams,
    timeLogActionIcon,
    timeLogActionLabel,
    type TimeLogEventDTO,
    timeLogEventSourceIcon,
    timeLogEventSourceLabel,
    toDateString,
    toTimeString,
} from "@pentacode/core";
import { css, html, LitElement, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import "../../../../components/avatar/avatar";
import "../../../../elements/popover";
import "../../../../elements/time-log-event-details";
import { app, router } from "../../../../init";
import { shared } from "../../../../styles";
import "./base-tile";
import type { DashboardTile } from "./base-tile";

const mergeDashboardSettings = deepmerge();

type TimeLogEventWithComment = TimeLogEventDTO & { comment: string };

@customElement("ptc-dashboard-tile-time-log-comments")
export class DashboardTileTimeLogComments extends LitElement {
    @property({ attribute: false })
    size: { inline: number; block: number } = app.settings.dashboard.tiles.timeLogComments.size;

    @property({ attribute: false })
    days: number = app.settings.dashboard.tiles.timeLogComments.settings!.lookbehindDays;

    static styles = [
        shared,
        css`
            :host {
                display: contents;
            }

            .event-list {
                display: flex;
                flex-direction: column;
                overflow-y: auto;
                gap: 0.5em;
                font-size: 0.75rem;
            }

            .date-group {
                display: flex;
                flex-direction: column;
                gap: 0.5em;
                container-type: inline-size;

                .date-header {
                    font-weight: 600;
                    color: var(--color-text-secondary);
                    font-size: 1.25em;
                    position: sticky;
                    top: 0;
                    background: var(--color-bg);
                    z-index: 1;
                    padding-inline: var(--tile-content-padding, 1rem);
                    padding-block: 0.5em;
                }
            }

            .event-item {
                display: grid;
                grid-template-columns: min-content auto min-content;
                grid-template-areas:
                    "avatar avatar time"
                    "position action action"
                    "comment comment comment";
                gap: 0.5em;
                transition: all 0.2s ease;
                border-radius: var(--border-radius);
                padding: calc(var(--tile-content-padding, 1rem) / 2);
                margin-inline: calc(var(--tile-content-padding, 1rem) / 2);
                font-weight: lighter;
                background: none;

                @container (width < 250px) {
                    grid-template-areas:
                        "action time time"
                        "avatar avatar avatar"
                        "position position position"
                        "comment comment comment";
                }

                &:focus-visible {
                    outline: 1px solid var(--primary);
                }

                &:hover {
                    background: var(--shade-1);
                    box-shadow: none;
                    color: var(--primary);
                    cursor: pointer;
                }

                .event-avatar {
                    grid-area: avatar;
                    display: flex;
                    align-items: center;
                    justify-content: start;
                    gap: 0.5em;

                    ptc-avatar {
                        width: max-content;
                        font-size: 0.6em;
                    }
                }

                .event-action {
                    grid-area: action;
                    display: flex;
                    align-items: center;
                    gap: 0.25em;
                    place-self: center end;
                    color: var(--primary);
                }

                .event-time {
                    grid-area: time;
                    display: flex;
                    white-space: nowrap;
                    align-items: center;
                    font-weight: bold;
                    place-content: center end;

                    ptc-popover {
                        font-size: 0.8em;
                        font-weight: lighter;
                    }
                }

                .event-position {
                    grid-area: position;
                    width: min-content;
                    place-self: center start;
                }

                .event-comment {
                    grid-area: comment;
                    padding: 0.5em;
                    background: color-mix(in hsl, var(--primary) 5%, transparent);
                    border-radius: calc(var(--border-radius) * 0.75);
                    color: var(--color-text-secondary);
                    text-align: start;

                    ptc-ui-icon {
                        flex-shrink: 0;
                    }
                }
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 1rem;
                height: 100%;
                padding: 2rem;
                text-align: center;
                color: var(--color-text-secondary);

                .emoji {
                    font-size: 3rem;
                }
            }

            ptc-time-log-event-details::part(comment) {
                display: none;
            }
        `,
    ];

    private _timeLogEventsTask = new Task(this, {
        task: async ([days]) => {
            const { from: fromTime, to: toTime } = getDateRangeFromDays(-1 * days);

            const events = (await app.api.getTimeLogEvents(
                new GetTimeLogEventsParams({
                    fromTime,
                    toTime,
                    onlyWithComments: true,
                })
            )) as TimeLogEventWithComment[];

            const groupedByDate = Object.groupBy(events, (event) => toDateString(event.time));
            return groupedByDate;
        },
        args: () => [this.days],
    });

    private _navigateToTimeEntry(timeLogEvent: TimeLogEventWithComment) {
        const { from, to } = getMonthRange(toDateString(timeLogEvent.time));

        router.go(`employees/${timeLogEvent.employeeId}/time`, {
            from,
            to,
            timeEntry: timeLogEvent.timeEntryId || "",
        });
    }

    private _renderEvent(event: TimeLogEventWithComment) {
        const actionInfo = timeLogActionIcon(event.action);
        const employee = app.employees.find((e) => e.id === event.employeeId);
        const position = event.positionId ? app.positions.find((p) => p.id === event.positionId) : null;

        return html`
            <button
                class="event-item"
                data-time-entry-id=${event.timeEntryId || ""}
                @click=${() => this._navigateToTimeEntry(event)}
            >
                <div class="event-avatar">
                    <ptc-avatar .employee=${employee} size="small"></ptc-avatar>
                    ${employee?.name}
                </div>

                <div class="event-action">
                    <ptc-ui-icon name="${actionInfo}"></ptc-ui-icon>
                    <span>${timeLogActionLabel(event.action)}</span>
                </div>
                ${position
                    ? html` <div
                          class="smaller pill ellipsis event-position"
                          style="--color-highlight: ${app.getPositionColor(position)}"
                      >
                          ${position.name}
                      </div>`
                    : nothing}

                <time class="event-time" datetime="${toTimeString(event.time)}">
                    ${event.image || event.location
                        ? html`
                              <div>
                                  ${event.image
                                      ? html` <ptc-ui-icon name="icon.object.proof-photo"></ptc-ui-icon>`
                                      : nothing}
                                  ${event.location
                                      ? html` <ptc-ui-icon name="icon.object.location"></ptc-ui-icon>`
                                      : nothing}
                              </div>
                              <ptc-popover trigger="hover" alignment="bottom" non-interactive>
                                  <ptc-time-log-event-details .event=${event}></ptc-time-log-event-details>
                              </ptc-popover>
                          `
                        : nothing}
                    <ptc-ui-icon name="${timeLogEventSourceIcon(event.source)}"></ptc-ui-icon>
                    <ptc-popover trigger="hover" alignment="bottom" non-interactive>
                        ${timeLogEventSourceLabel(event.source)}
                    </ptc-popover>
                    ${toTimeString(event.time)}</time
                >

                <div class="event-comment">
                    <ptc-ui-icon name="icon.object.comment"></ptc-ui-icon>
                    <span>${event.comment}</span>
                </div>
            </button>
        `;
    }

    private _renderDateGroup(date: string, events: TimeLogEventWithComment[]) {
        return html`
            <div class="date-group">
                <div class="date-header">${formatDate(new Date(date), false)}</div>
                ${events.map((event) => this._renderEvent(event))}
            </div>
        `;
    }

    private _formRef: Ref<HTMLFormElement> = createRef();
    private _tileRef: Ref<DashboardTile> = createRef();

    private _saveSettings(ev: SubmitEvent | InputEvent) {
        ev.preventDefault();

        const data = new FormData(this._formRef.value);

        const lookbehindDays = Number(data.get("lookbehindDays"));

        app.updateSettings({
            dashboard: mergeDashboardSettings(app.settings.dashboard, {
                tiles: {
                    timeLogComments: {
                        settings: { lookbehindDays },
                    },
                },
            }),
        })
            .then(() => {
                this.requestUpdate();
                if (this._tileRef.value) {
                    this.days = lookbehindDays;
                    this._tileRef.value.settingsOpen = false;
                }
            })
            .catch((e) => {
                console.error(e);
            });
    }

    private _renderSettings() {
        return html`
            <form slot="settings" ${ref(this._formRef)} @submit=${this._saveSettings} id="tile.timeLogComments">
                <fieldset>
                    <legend>Zeitraum</legend>

                    <div class="field">
                        <div class="input-container">
                            <input
                                name="lookbehindDays"
                                id="tile.timeLogComments.lookbehindDays"
                                type="number"
                                min="0"
                                max="30"
                                maxlength="2"
                                style="width: 6ch;text-align: right;"
                                placeholder=${defaultDashboardConfig.tiles.timeLogComments.settings!.lookbehindDays.toString()}
                                .value=${app.settings.dashboard.tiles.timeLogComments.settings!.lookbehindDays.toString()}
                                .defaultValue=${defaultDashboardConfig.tiles.timeLogComments.settings!.lookbehindDays.toString()}
                            />
                            Tage in der Vergangenheit
                        </div>
                    </div>
                </fieldset>

                <!-- workaround for some browsers needing a explicit submit element -->
                <input type="submit" style="display: none;" />
            </form>
        `;
    }

    private _getLabel = (days: number) => {
        if (days === 0) return "von heute";
        return `der letzten ${days + 1} Tage`;
    };
    private _getEmptyMessage = (days: number) => {
        if (days === 0) return "Keine Schichtkommentare von heute";
        return `Keine Schichtkommentare in den letzten ${days + 1} Tagen`;
    };

    render() {
        return html`
            <ptc-dashboard-tile
                ${ref(this._tileRef)}
                style="--tile-icon-color: var(--blue)"
                label=${DASHBOARD_CONFIG.tiles.timeLogComments.label}
                .subLabel=${this._getLabel(this.days)}
                .size=${this.size}
                .settingsForm=${this._formRef.value}
                tileType="timeLogComments"
                icon="icon.object.comment"
                @tile-size-change=${(e: CustomEvent) => {
                    this.size = e.detail.size;
                }}
            >
                ${this._timeLogEventsTask.render({
                    initial: () => html``,
                    pending: () => html`
                        <ptc-ui-spinner
                            class="double-padded place-self-center"
                            active
                            style="font-size: 1rem"
                        ></ptc-ui-spinner>
                    `,
                    error: () =>
                        html`<div class="empty-state">
                            <span class="emoji">⚠️</span>
                            <p>Ereignisse konnten nicht geladen werden</p>
                        </div>`,
                    complete: (groupedEvents) => {
                        const dates = Object.keys(groupedEvents);
                        if (dates.length === 0) {
                            return html`<div class="empty-state">
                                <span class="emoji">🕐</span>
                                <p>${this._getEmptyMessage(this.days)}</p>
                            </div>`;
                        }
                        return html`<div class="event-list">
                            ${dates.map((date) =>
                                this._renderDateGroup(date, groupedEvents[date as keyof typeof groupedEvents] || [])
                            )}
                        </div>`;
                    },
                })}
                ${this._renderSettings()}
            </ptc-dashboard-tile>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-dashboard-tile-time-log-comments": DashboardTileTimeLogComments;
    }
}
